import _get from 'lodash/get';
import _isEmpty from 'lodash/isEmpty';
// Serialization
import { deserialize, serialize } from 'serializr';
import { addressDescCC } from '../../utils/Tools';
import { partnerSchema, partnerFinancialInformationSchema } from './schemas';
// Fragments
import { partnerFragment } from './fragments';
// Custom
import { SEARCHFIELD_TYPES } from '../../constants/App';

/**
 * Serialization
 */
const deserializePartner = (data) => deserialize(partnerSchema, data);

const serializePartner = ({ name, financialInformation }) => ({
  ...serialize(partnerSchema, {
    name,
    financialInformation,
  }),
});

const serializePartnerFinancialInformation = ({
  address,
  emailForCommissionInvoices,
  europeanVatNumber,
  iban,
  nationalIdentifier,
}) => ({
  ...serialize(partnerFinancialInformationSchema, {
    address,
    emailForCommissionInvoices,
    europeanVatNumber,
    iban,
    nationalIdentifier,
  }),
});

/**
 * Utils
 */
// get financial info
const getFinancialInformation = (partner) => _get(partner, 'financialInformation', {});
const getEmailForCommissionInvoices = (partner) => _get(getFinancialInformation(partner), 'emailForCommissionInvoices', '');
const getInvoiceIdentifier = (partner) => _get(getFinancialInformation(partner), 'invoiceIdentifier', '');
const getAddressDesc = (partner) => {
  const address = _get(getFinancialInformation(partner), 'address', '');
  return address ? addressDescCC(address) : '';
};

/**
 * GraphQL
 */
const quelistPartners = `
  query ListPartners($input: ListPartnersInput!, $pagination: PaginationInput, $search: ListPartnersSearchInput, $sort: [ListPartnersSortInput]) {
    listPartners(input: $input, pagination: $pagination, search: $search, sort: $sort) {
      entries {
        ...partnerFragment
      },
      pageNumber,
      pageSize,
      totalEntries,
      totalPages
    }
  }

  ${partnerFragment}
`;

const mutCreatePartner = `
  mutation CreatePartner($input: CreatePartnerInput!) {
    createPartner(input: $input) {
      id
    }
  }
`;

const mutUpdatePartnerFinancialInformation = `
  mutation UpdatePartnerFinancialInformation($input: UpdatePartnerFinancialInformationInput!) {
    updatePartnerFinancialInformation(input: $input) {
      ...partnerFragment
    }
  }

  ${partnerFragment}
`;

/**
 * Model
 */
const partner = {
  // Name and icon
  name: 'partner',
  icon: 'partner',

  // Serialization
  deserialize: deserializePartner,
  serialize: serializePartner,
  serializeFinancialInformation: serializePartnerFinancialInformation,

  // Lists

  // Getters

  // Utils
  getFinancialInformation,
  getEmailForCommissionInvoices,
  getAddressDesc,
  getInvoiceIdentifier,

  // List features
  listFeatures: {
    // Default list params
    defaultParams: {
      pagination: {
        page: 1,
        pageSize: 20,
      },
      sort: [{ field: 'name', dir: 'asc' }],
    },

    // Columns
    columnsQuantity: () => ({
      xs: 1,
      sm: 1,
      md: 2,
      lg: 4,
      xl: 4,
    }),
    columns: [
      {
        key: 'name',
        labelId: 'field.name',
        field: 'name',
        sortable: true,
      },
      {
        key: 'address',
        labelId: 'field.address',
        field: 'address',
        sortable: false,
        className: 'hidden-md-down',
      },
      {
        key: 'emailForCommissionInvoices',
        labelId: 'field.emailForCommissionInvoices',
        field: 'emailForCommissionInvoices',
        sortable: false,
        className: 'hidden-md-down',
      },
      {
        key: 'invoiceIdentifier',
        labelId: 'field.invoiceIdentifier',
        field: 'invoiceIdentifier',
        sortable: false,
        className: 'hidden-sm-down',
      },
    ],

    // Search fields
    searchFields: [
      {
        key: 'name',
        labelId: 'field.name',
        field: 'name',
        initial: '',
        isUndefined: (value) => _isEmpty(value),
        needGetOnInit: false,
        type: SEARCHFIELD_TYPES.string,
        filter: {
          field: 'name',
        },
      },
      {
        key: 'emailForCommissionInvoices',
        labelId: 'field.emailForCommissionInvoices',
        field: 'emailForCommissionInvoices',
        initial: '',
        isUndefined: (value) => _isEmpty(value),
        needGetOnInit: false,
        type: SEARCHFIELD_TYPES.string,
        filter: {
          field: 'emailForCommissionInvoices',
        },
      },
      {
        key: 'nationalIdentifier',
        labelId: 'field.nationalIdentifier',
        field: 'nationalIdentifier',
        initial: '',
        isUndefined: (value) => _isEmpty(value),
        needGetOnInit: false,
        type: SEARCHFIELD_TYPES.string,
        filter: {
          field: 'nationalIdentifier',
        },
      },
      {
        key: 'europeanVatNumber',
        labelId: 'field.europeanVatNumber',
        field: 'europeanVatNumber',
        initial: '',
        isUndefined: (value) => _isEmpty(value),
        needGetOnInit: false,
        type: SEARCHFIELD_TYPES.string,
        filter: {
          field: 'europeanVatNumber',
        },
      },
      {
        key: 'invoiceIdentifier',
        labelId: 'field.invoiceIdentifier',
        field: 'invoiceIdentifier',
        initial: '',
        isUndefined: (value) => _isEmpty(value),
        needGetOnInit: false,
        type: SEARCHFIELD_TYPES.string,
        filter: {
          field: 'invoiceIdentifier',
        },
      },
      {
        key: 'iban',
        labelId: 'field.iban',
        field: 'iban',
        initial: '',
        isUndefined: (value) => _isEmpty(value),
        needGetOnInit: false,
        type: SEARCHFIELD_TYPES.string,
        filter: {
          field: 'iban',
        },
      },
    ],
  },

  // GraphQL
  quelistPartners,
  mutCreatePartner,
  mutUpdatePartnerFinancialInformation,
};

module.exports = partner;



// WEBPACK FOOTER //
// ./models/cpm/partner.js